import React, { useCallback, useEffect, useId, useState } from 'react'

interface UnlimitedInputProps {
    value?: string | number
    onChange?: (value: string | null) => void
    placeholder?: string
    className?: string
    disabled?: boolean
    type?: 'number' | 'text'
    step?: string | number
    min?: string | number
    label?: string
}

// Helper function to format numbers with commas
const formatNumber = (num: string | number): string => {
    if (num === '' || num === null || num === undefined) return ''

    const numStr = String(num)
    if (numStr === '' || numStr === '0') return numStr

    // Handle negative numbers
    const isNegative = numStr.startsWith('-')
    const cleanNum = numStr.replace(/[^0-9.]/g, '')

    if (!cleanNum || cleanNum === '.') return numStr // Return original if invalid

    const parts = cleanNum.split('.')
    const integerPart = parts[0] || '0'
    const decimalPart = parts[1]

    // Always format if integer part has digits (including just '0')
    if (integerPart && integerPart.length > 0) {
        const formattedInteger = integerPart.replace(
            /\B(?=(\d{3})+(?!\d))/g,
            ','
        )
        const result =
            decimalPart !== undefined
                ? `${formattedInteger}.${decimalPart}`
                : formattedInteger
        return isNegative ? `-${result}` : result
    }

    return numStr
}

const UnlimitedInput: React.FC<UnlimitedInputProps> = ({
    value,
    onChange,
    placeholder = '',
    className = '',
    disabled = false,
    step,
    min,
    label,
}) => {
    const id = useId()
    const [isUnlimited, setIsUnlimited] = useState(false)
    const [inputValue, setInputValue] = useState('')
    const [isFocused, setIsFocused] = useState(false)

    // Check if value indicates unlimited
    useEffect(() => {
        if (value === '' || value === null || value === undefined) {
            setIsUnlimited(false)
            setInputValue('')
        } else if (
            value === 'unlimited' ||
            value === '∞' ||
            Number(value) >= 999999999
        ) {
            setIsUnlimited(true)
            setInputValue('')
        } else {
            setIsUnlimited(false)
            setInputValue(formatNumber(String(value)))
        }
    }, [value])

    const handleToggleUnlimited = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()

            setIsUnlimited((prev) => {
                const newIsUnlimited = !prev

                if (newIsUnlimited) {
                    // Switching to unlimited
                    setInputValue('')
                    onChange?.('999999999')
                } else {
                    // Switching to limited
                    setInputValue('0')
                    onChange?.('0')
                }

                return newIsUnlimited
            })
        },
        [onChange]
    )

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value

        // Allow empty value
        if (rawValue === '') {
            setInputValue('')
            onChange?.('')
            return
        }

        // Remove all non-numeric characters except decimal point and minus
        const numericValue = rawValue.replace(/[^0-9.-]/g, '')

        // Handle special cases - keep raw input for better UX while typing
        if (
            numericValue === '' ||
            numericValue === '-' ||
            numericValue === '.'
        ) {
            setInputValue(rawValue)
            onChange?.(numericValue === '' ? '' : numericValue)
            return
        }

        // For valid numbers, allow unformatted input temporarily
        const numberValue = Number(numericValue)
        if (!isNaN(numberValue)) {
            // Set the clean numeric value (no formatting during typing)
            setInputValue(numericValue)
            onChange?.(numericValue)
        }
    }

    // Format on blur to avoid cursor jumping during typing
    const handleBlur = () => {
        setIsFocused(false)

        // Format the final value when user finishes typing
        if (inputValue && !isUnlimited) {
            const numericValue = inputValue.replace(/[^0-9.-]/g, '')
            if (numericValue && !isNaN(Number(numericValue))) {
                const formattedValue = formatNumber(numericValue)
                setInputValue(formattedValue)
            }
        }
    }

    // Check if we should show floating label (has value, is focused, or is unlimited)
    const hasValue = (value?.toString()?.length || 0) > 0 || isUnlimited
    const shouldFloat = hasValue || isFocused

    return (
        <div className={className}>
            {/* Input Container */}
            <div className="relative h-[36px]">
                {/* Floating Label */}
                {label && (
                    <label
                        htmlFor={id}
                        className={`
                            absolute left-3 transition-all duration-200 ease-in-out pointer-events-none
                            ${
                                shouldFloat
                                    ? '-top-2 text-xs text-gray-500 bg-white px-1 z-10'
                                    : 'top-2.5 text-sm text-gray-400'
                            }
                        `}
                    >
                        {label}
                    </label>
                )}

                {/* Main Input */}
                <input
                    id={id}
                    type={isUnlimited ? 'text' : 'text'}
                    inputMode={isUnlimited ? 'text' : 'numeric'}
                    value={isUnlimited ? '∞ UNLIMITED' : inputValue}
                    onChange={handleInputChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={handleBlur}
                    placeholder={shouldFloat ? placeholder : ''}
                    disabled={disabled || isUnlimited}
                    step={step}
                    min={min}
                    className={`
                        w-full h-full px-3 py-2 pr-12 text-sm border rounded-[4px]
                        transition-all duration-200 ease-in-out
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                        ${
                            isUnlimited
                                ? 'bg-purple-50 border-purple-300 text-purple-700 font-medium'
                                : 'bg-white border-gray-300 hover:border-gray-400 focus:border-blue-500'
                        }
                        ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}
                        ${label ? 'pt-3' : ''}
                    `}
                    readOnly={isUnlimited}
                />

                {/* Unlimited Toggle Button */}
                <button
                    type="button"
                    onClick={handleToggleUnlimited}
                    disabled={disabled}
                    className={`
                        absolute right-2 top-1/2 -translate-y-1/2
                        px-2.5 py-1 text-xs font-medium rounded transition-all duration-200
                        ${
                            isUnlimited
                                ? 'bg-purple-500 text-white shadow-sm hover:bg-purple-600 active:bg-purple-700'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 active:bg-gray-300'
                        }
                        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-105'}
                    `}
                    title={isUnlimited ? 'Set limited value' : 'Set unlimited'}
                >
                    ∞
                </button>
            </div>
        </div>
    )
}

export default UnlimitedInput
